import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { COMPANY, NAV } from "../lib/content";
import { subscribeNewsletter } from "../lib/api";

const SOCIALS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/mmediacorp",
    Icon: Instagram,
    testid: "footer-social-instagram",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/mmediacorp",
    Icon: Facebook,
    testid: "footer-social-facebook",
  },
  {
    label: "YouTube",
    href: "https://youtube.com/@mmediacorp",
    Icon: Youtube,
    testid: "footer-social-youtube",
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      const res = await subscribeNewsletter(email);
      toast.success(res.already_subscribed ? "You're already on the list." : "Welcome to the dossier.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="bg-ink text-cream relative" data-testid="site-footer">
      <div className="container-edge py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <h3 className="font-display text-6xl md:text-8xl leading-[0.85] uppercase">
            Let&apos;s<br/>create<br/><span className="serif-italic font-serif normal-case text-cream/90">your legacy.</span>
          </h3>
          <Link to="/apply" className="btn-ghost mt-10 border-cream/60 text-cream hover:bg-cream hover:text-ink" data-testid="footer-cta">
            Request an application →
          </Link>
        </div>

        <div className="md:col-span-3">
          <div className="overline text-cream/50 mb-4">Index</div>
          <ul className="flex flex-col gap-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="font-serif text-2xl text-cream hover:text-cream/60 transition-colors">{n.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline text-cream/50 mb-4">Studio</div>
          <p className="font-serif text-xl leading-relaxed">
            620 Newport Center Dr., Suite #1100<br/>
            Newport Beach, CA 92660
          </p>
          <a href={`mailto:${COMPANY.email}`} className="block mt-3 overline text-cream hover:text-cream/60 py-2">{COMPANY.email}</a>

          {/* Social */}
          <div className="overline text-cream/50 mt-8 mb-3">Follow</div>
          <div className="flex items-center gap-3" data-testid="footer-socials">
            {SOCIALS.map(({ label, href, Icon, testid }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`M Media Corp on ${label}`}
                data-testid={testid}
                className="grid place-items-center w-10 h-10 border border-cream/25 text-cream/80 hover:text-cream hover:bg-cream/10 hover:border-cream transition-colors"
              >
                <Icon className="w-4 h-4" strokeWidth={1.5}/>
              </a>
            ))}
          </div>

          <div className="overline text-cream/50 mt-10 mb-3">The Dossier</div>
          <form onSubmit={onSubscribe} className="flex items-end gap-3 border-b border-cream/30 pb-2" data-testid="footer-newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 bg-transparent text-cream placeholder:text-cream/40 outline-none py-2"
              data-testid="footer-newsletter-input"
            />
            <button type="submit" disabled={loading} className="overline text-cream hover:text-cream/60" data-testid="footer-newsletter-submit">
              {loading ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="container-edge py-6 border-t border-cream/15 flex flex-col md:flex-row md:items-center justify-between gap-4 overline text-cream/50">
        <div className="flex items-center gap-4">
          <img src="/brand/mmedia-logo-light.png" alt="M Media Corp" className="h-8 w-auto opacity-70" draggable="false" />
          <span>© {new Date().getFullYear()} M Media Corp. All rights reserved.</span>
        </div>
        <span>Newport Beach · Los Angeles · New York</span>
      </div>
    </footer>
  );
}
